<template>
  <div 
    class="section-row-wrapper" 
    :class="{ 'section-row-wrapper--active': isMenuOpen }"
  >
    <div
      :class="[
        rowClass,
        { 
          [`${rowClass}--last`]: isLast,
          [`${rowClass}--elevated`]: isMenuOpen
        }
      ]"
      @click="handleClick"
    >
      <slot />
      
      <!-- Menu button, Lock icon, or Chevron -->
      <template v-if="noAction">
        <!-- no action area -->
      </template>
      <template v-else-if="!hideActions">
        <button 
          v-if="canEdit || showMenu"
          :class="`${rowClass}-menu`"
          @click.stop="openContextMenu($event)"
        >
          <v-icon size="20">mdi-dots-vertical</v-icon>
        </button>
        <v-icon v-else-if="showLock" :class="`${rowClass}-lock`" size="18" style="opacity: 0.4;">mdi-lock</v-icon>
        <v-icon v-else :class="`${rowClass}-chevron`" size="18">mdi-chevron-right</v-icon>
      </template>
      <v-icon v-else :class="`${rowClass}-chevron`" size="18" style="opacity: 0.5;">mdi-login</v-icon>
    </div>

    <!-- Context Menu -->
    <Teleport to="body">
      <Transition name="fade">
        <div 
          v-if="isMenuOpen" 
          class="context-overlay" 
          @click="closeContextMenu"
        ></div>
      </Transition>
      <Transition name="context-menu">
        <div 
          v-if="isMenuOpen"
          ref="menuRef"
          class="context-menu-content-slot"
          :style="contextMenuStyle"
        >
          <slot name="menu" :close="closeContextMenu" />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onUnmounted, nextTick } from 'vue'

const props = withDefaults(defineProps<{
  canEdit?: boolean
  showMenu?: boolean
  showLock?: boolean
  hideActions?: boolean
  noAction?: boolean
  isLast?: boolean
  rowClass?: string
}>(), {
  canEdit: false,
  showMenu: false,
  showLock: false,
  hideActions: false,
  noAction: false,
  isLast: false,
  rowClass: 'ios-row'
})

const emit = defineEmits<{
  click: []
  menuOpen: []
  menuClose: []
}>()

const isMenuOpen = ref(false)
const contextMenuStyle = ref<Record<string, string>>({})
const menuRef = ref<HTMLElement | null>(null)
const pendingRect = ref<{ top: number; bottom: number; right: number } | null>(null)

// Lock/unlock body scroll
const lockBodyScroll = () => {
  document.documentElement.style.overflow = 'hidden'
}

const unlockBodyScroll = () => {
  document.documentElement.style.overflow = ''
}

watch(isMenuOpen, (newVal) => {
  if (newVal) {
    lockBodyScroll()
    emit('menuOpen')
  } else {
    unlockBodyScroll()
    emit('menuClose')
  }
})

onUnmounted(() => {
  if (isMenuOpen.value) {
    unlockBodyScroll()
  }
})

const handleClick = () => {
  if (isMenuOpen.value) {
    closeContextMenu()
    return
  }
  emit('click')
}

const openContextMenu = (event: MouseEvent) => {
  if (isMenuOpen.value) {
    closeContextMenu()
    return
  }
  
  const rowEl = (event.target as HTMLElement).closest(`.${props.rowClass}`) as HTMLElement
  if (!rowEl) return
  
  const rect = rowEl.getBoundingClientRect()
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  const GAP = 8
  const expandedRight = rect.right

  // Initially position below the row; will adjust after measuring
  contextMenuStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + GAP}px`,
    right: `${viewportWidth - expandedRight}px`,
    left: 'auto',
    zIndex: '10001',
    visibility: 'hidden'
  }
  
  pendingRect.value = { top: rect.top, bottom: rect.bottom, right: expandedRight }
  isMenuOpen.value = true

  nextTick(() => {
    const menuEl = menuRef.value
    if (!menuEl || !pendingRect.value) {
      // Fallback: just make visible
      contextMenuStyle.value.visibility = 'visible'
      return
    }

    const menuHeight = menuEl.offsetHeight
    const { top, bottom, right } = pendingRect.value
    const showAbove = bottom + menuHeight + GAP > viewportHeight

    const menuTop = showAbove
      ? top - menuHeight - GAP
      : bottom + GAP

    contextMenuStyle.value = {
      position: 'fixed',
      top: `${menuTop}px`,
      right: `${viewportWidth - right}px`,
      left: 'auto',
      zIndex: '10001'
    }
    pendingRect.value = null
  })
}

const closeContextMenu = () => {
  isMenuOpen.value = false
}

// Expose for parent to close menu programmatically
defineExpose({
  closeContextMenu
})
</script>

<style lang="scss" scoped>
.section-row-wrapper {
  position: relative;

  &--active {
    z-index: 10000;
  }
}
</style>
